/**
 * Kurva product section (<kv-product>): gallery, offer cards, ship-by date, add to cart.
 *
 * Cart integration mirrors Horizon's product-form.js:
 *   1. dispatch a CartLinesUpdateEvent (from '@shopify/events') carrying a deferred promise,
 *   2. POST /cart/add.js with `sections` = every <cart-items-component data-section-id>,
 *   3. resolve the promise with the refreshed cart + `sections` HTML, so the cart drawer
 *      morphs its content and <cart-icon> updates the header bubble,
 *   4. open <theme-drawer id="cart-drawer">.
 * If the events module or the drawer is missing, it falls back to the /cart page.
 *
 * Free gifts: the selected offer radio lists its gift variants in data-gift-variants ("id,id", from the
 * offer_perks block's gift_product / gift_product_2, see snippets/kv-product-offer.liquid). The same
 * /cart/add.js call adds each of them, quantity 1, with the line property `_kv_gift_for` = every offer
 * variant id of this section that grants THAT gift (so a gift only one offer grants follows that offer
 * alone). A gift is only added when the cart (fetched first) does not already grant it for this offer
 * (see giftItemsFor: a gift line from the other product page is re-sent with both lists). If the combined
 * add is refused, the offer is retried alone so a gift problem never blocks the sale, then each gift is
 * tried on its own (one problem gift does not cost the others). The cart drawer
 * (snippets/kv-cart-state.liquid + assets/kv-cart.js) removes each gift once no offer granting it is
 * left and keeps its quantity at 1. Its reservation timer (<kv-gift-timer>) removes the gifts when time
 * is up; nothing here adds them back on its own: gifts are only ever added by an explicit add to cart
 * (form submit / sticky bar), which then starts a fresh reservation.
 *
 * Emits `kv:offer-change` on document ({ detail: state }) for the sticky add-to-cart bar.
 */
(() => {
  if (customElements.get('kv-product')) return;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class KvProduct extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      this.busy = false;
      this.index = 0;
      this.target = null;

      try {
        const raw = this.querySelector('[data-kvp-json]');
        this.data = raw ? JSON.parse(raw.textContent) : { variants: [] };
      } catch (e) {
        this.data = { variants: [] };
      }

      this.form = this.querySelector('form.kvp-form');
      this.atc = this.querySelector('[data-atc]');
      this.errorEl = this.querySelector('[data-error]');
      this.liveEl = this.querySelector('[data-live]');

      this.initGallery();
      this.initOffers();
      this.initShipDate();
      this.initFaq();
      this.initEditor();
    }

    /* ------------------------------------------------------------------- FAQ */

    // Only one buy-box FAQ open at a time (fallback for browsers without <details name>).
    initFaq() {
      const { signal } = this.ac;
      this.addEventListener(
        'toggle',
        (event) => {
          const item = event.target;
          if (!(item instanceof HTMLDetailsElement) || !item.matches('details[data-faq]') || !item.open) return;
          this.querySelectorAll('details[data-faq][open]').forEach((other) => {
            if (other !== item) other.open = false;
          });
        },
        { capture: true, signal }
      );
    }

    disconnectedCallback() {
      this.ac?.abort();
      cancelAnimationFrame(this.raf);
      clearTimeout(this.targetTimer);
    }

    /* ------------------------------------------------------------------ Gallery */

    initGallery() {
      const { signal } = this.ac;
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.querySelectorAll('[data-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      if (this.slides.length < 2) return;

      this.track.addEventListener(
        'scroll',
        () => {
          cancelAnimationFrame(this.raf);
          this.raf = requestAnimationFrame(() => this.onTrackScroll());
        },
        { passive: true, signal }
      );

      this.track.addEventListener(
        'scrollend',
        () => {
          this.target = null;
          this.onTrackScroll();
        },
        { signal }
      );

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.goTo(this.index - 1), { signal });
      this.querySelector('[data-next]')?.addEventListener('click', () => this.goTo(this.index + 1), { signal });

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.goTo(i), { signal });
      });

      this.track.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.goTo(this.index + 1);
          } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.goTo(this.index - 1);
          }
        },
        { signal }
      );

      // Fade the thumbnail row edges while more thumbnails are hidden in that direction.
      const row = this.querySelector('[data-thumbs]');
      const updateRow = () => {
        if (!row) return;
        const max = row.scrollWidth - row.clientWidth;
        row.classList.toggle('has-more-start', row.scrollLeft > 4);
        row.classList.toggle('has-more-end', max > 4 && row.scrollLeft < max - 4);
      };
      row?.addEventListener('scroll', updateRow, { passive: true, signal });
      updateRow();

      // Keep the current slide aligned when the viewport (and so the slide width) changes.
      if ('ResizeObserver' in window) {
        let lastWidth = this.track.clientWidth;
        const ro = new ResizeObserver(() => {
          updateRow();
          const w = this.track.clientWidth;
          if (w && w !== lastWidth) {
            lastWidth = w;
            this.track.scrollTo({ left: this.index * w, behavior: 'auto' });
          }
        });
        ro.observe(this.track);
        signal.addEventListener('abort', () => ro.disconnect());
      }
    }

    onTrackScroll() {
      const width = this.track.clientWidth || 1;
      const i = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / width)));
      if (this.target !== null) {
        if (i !== this.target) return;
        this.target = null;
      }
      if (i !== this.index) this.setActive(i);
    }

    goTo(i, smooth = true) {
      if (!this.track || !this.slides?.length) return;
      const n = this.slides.length;
      const next = ((i % n) + n) % n;
      this.target = next;
      clearTimeout(this.targetTimer);
      this.targetTimer = setTimeout(() => {
        this.target = null;
      }, 1200);
      this.track.scrollTo({
        left: next * this.track.clientWidth,
        behavior: smooth && !reducedMotion() ? 'smooth' : 'auto',
      });
      this.setActive(next);
    }

    setActive(i) {
      this.index = i;
      this.thumbs.forEach((thumb, t) => {
        const on = t === i;
        thumb.classList.toggle('is-active', on);
        if (on) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.dots.forEach((dot, d) => dot.classList.toggle('is-active', d === i));

      const thumb = this.thumbs[i];
      const row = thumb?.parentElement;
      if (thumb && row && row.scrollWidth > row.clientWidth) {
        const left = thumb.offsetLeft - (row.clientWidth - thumb.offsetWidth) / 2;
        row.scrollTo({ left: Math.max(0, left), behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
    }

    goToMedia(mediaId) {
      if (!mediaId || !this.slides) return;
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1 && i !== this.index) this.goTo(i);
    }

    /* ------------------------------------------------------------------- Offers */

    initOffers() {
      const { signal } = this.ac;
      this.offerInputs = Array.from(this.querySelectorAll('input[data-offer-input]'));
      this.planInputs = Array.from(this.querySelectorAll('input[data-plan-input]'));

      [...this.offerInputs, ...this.planInputs].forEach((input) => {
        input.addEventListener('change', () => this.update(true), { signal });
      });

      this.form?.addEventListener(
        'submit',
        (event) => {
          event.preventDefault();
          this.addToCart();
        },
        { signal }
      );

      this.update(false);
    }

    get selectedInput() {
      return this.offerInputs.find((i) => i.type === 'hidden' || i.checked) || null;
    }

    get variantId() {
      const checked = this.selectedInput;
      return checked ? checked.value : null;
    }

    get sellingPlanId() {
      const checked = this.planInputs.find((i) => i.checked);
      return checked && checked.value ? checked.value : null;
    }

    variantData(id) {
      return (this.data.variants || []).find((v) => String(v.id) === String(id)) || null;
    }

    /** Pricing for a variant, taking the selected selling plan into account. */
    pricing(v, planId) {
      if (!v) return null;
      if (planId && v.plans && v.plans[planId]) return v.plans[planId];
      return { price: v.price, compare: v.compare, save: v.save };
    }

    update(userInitiated) {
      const id = this.variantId;
      const v = this.variantData(id);
      const planId = this.sellingPlanId;

      // Refresh every card's price (a selling plan changes all of them).
      this.offerInputs.forEach((input) => {
        const card = input.nextElementSibling;
        if (!card || input.type === 'hidden') return;
        const cardVariant = this.variantData(input.value);
        const pr = this.pricing(cardVariant, planId);
        if (!pr) return;
        const priceEl = card.querySelector('[data-offer-price]');
        const compareEl = card.querySelector('[data-offer-compare]');
        const compareLabelEl = card.querySelector('[data-offer-compare-label]');
        const unitEl = card.querySelector('[data-offer-unit]');
        if (priceEl) priceEl.textContent = pr.price;
        if (compareEl) {
          compareEl.textContent = pr.compare || '';
          compareEl.hidden = !pr.compare;
        }
        if (compareLabelEl) compareLabelEl.hidden = !pr.compare;
        // The per-bottle line is computed in Liquid from the one-time price: hide it under a plan price.
        if (unitEl) unitEl.hidden = Boolean(cardVariant && pr.price !== cardVariant.price);
      });

      // Product without variants: a single price row instead of offer cards.
      const single = this.querySelector('[data-single]');
      if (single) {
        const pr = this.pricing(v, planId);
        if (pr) {
          const priceEl = single.querySelector('[data-single-price]');
          const compareEl = single.querySelector('[data-single-compare]');
          const saveEl = single.querySelector('[data-single-save]');
          const saveAmountEl = single.querySelector('[data-single-save-amount]');
          if (priceEl) priceEl.textContent = pr.price;
          if (compareEl) {
            compareEl.textContent = pr.compare || '';
            compareEl.hidden = !pr.compare;
          }
          if (saveEl) {
            if (saveAmountEl) saveAmountEl.textContent = pr.save || '';
            saveEl.hidden = !pr.save;
          }
        }
      }

      const available = Boolean(v && v.available);
      if (this.atc) {
        this.atc.disabled = !available;
        const label = this.atc.querySelector('[data-atc-label]');
        if (label) label.textContent = available ? this.atc.dataset.labelAdd : this.atc.dataset.labelSoldout;
      }

      if (userInitiated && id) {
        this.hideError();
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', id);
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (e) {
          /* no-op */
        }
        if (this.dataset.syncGallery === 'true' && v && v.media) this.goToMedia(v.media);
      }

      const pr = this.pricing(v, planId) || {};
      this.state = {
        sectionId: this.dataset.sectionId,
        productId: this.dataset.productId,
        variantId: id,
        sellingPlanId: planId,
        title: v ? v.title : '',
        available,
        price: pr.price || '',
        compare: pr.compare || '',
      };
      document.dispatchEvent(new CustomEvent('kv:offer-change', { detail: this.state }));
    }

    /* -------------------------------------------------------------- Ship date */

    initShipDate() {
      const el = this.querySelector('[data-ship-date]');
      if (!el) return;
      const days = Math.max(0, parseInt(this.dataset.shipDays || '1', 10) || 0);
      const d = new Date();
      let added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day !== 0 && day !== 6) added += 1;
      }
      while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);

      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      try {
        el.textContent = d.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' });
      } catch (e) {
        el.textContent = d.toDateString();
      }
    }

    /* ------------------------------------------------------------ Add to cart */

    setLoading(on, extra) {
      [this.atc, extra].forEach((btn) => {
        if (!btn) return;
        btn.classList.toggle('is-loading', on);
        btn.setAttribute('aria-busy', on ? 'true' : 'false');
      });
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    announce(text) {
      if (!this.liveEl) return;
      this.liveEl.textContent = text;
      setTimeout(() => {
        if (this.liveEl) this.liveEl.textContent = '';
      }, 3000);
    }

    cartUrl() {
      return (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || this.dataset.cartUrl || '/cart';
    }

    /**
     * Adds the selected offer. Public so the sticky bar can call it.
     * @param {HTMLElement} [triggerButton] - an extra button to put in the loading state.
     */
    async addToCart(triggerButton) {
      if (this.busy) return;
      const id = this.variantId;
      const v = this.variantData(id);
      if (!id || (v && !v.available)) return;

      this.busy = true;
      this.hideError();
      this.setLoading(true, triggerButton);

      const item = { id: Number(id), quantity: 1 };
      const planId = this.sellingPlanId;
      if (planId && v && v.plans && v.plans[planId]) item.selling_plan = Number(planId);

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      const [giftItems, Events] = await Promise.all([this.giftItemsFor(this.selectedInput), this.loadEvents()]);

      // Gifts listed first so the offer is the newest line (newest lines come first in the cart).
      // Either way the drawer shows gift rows after the other lines (CSS order in kv-cart-styles).
      const items = [...giftItems, item];
      const buildBody = (lineItems) => {
        const body = { items: lineItems };
        if (sectionIds.length) {
          body.sections = Array.from(new Set(sectionIds)).join(',');
          body.sections_url = window.location.pathname;
        }
        return body;
      };
      const unitCount = (lineItems) => lineItems.reduce((sum, line) => sum + line.quantity, 0);

      const drawer = document.querySelector('theme-drawer#cart-drawer');
      const canUseDrawer = Boolean(Events && drawer && typeof drawer.open === 'function' && sectionIds.length);

      let deferred = null;
      if (Events) {
        deferred = Events.CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new Events.CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
            promise: deferred.promise,
          })
        );
      }

      const addUrl =
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) ||
        `${this.dataset.cartAddUrl || '/cart/add'}.js`;

      const post = async (lineItems) => {
        const res = await fetch(addUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(buildBody(lineItems)),
        });
        const json = await res.json().catch(() => ({}));
        return { ok: res.ok && !json.status, json };
      };

      let added = false;
      try {
        let addedItems = items;
        let result = await post(items);
        if (!result.ok && giftItems.length) {
          // A gift could not be added (unavailable in this market, unpublished…): sell the offer anyway,
          // then try each gift on its own so one problem gift does not cost the others.
          addedItems = [item];
          result = await post(addedItems);
          if (result.ok && giftItems.length > 1) {
            for (const gift of giftItems) {
              const extra = await post([gift]).catch(() => null);
              if (extra && extra.ok) {
                addedItems = [gift, ...addedItems];
                // The latest successful response carries the freshest cart sections.
                result = extra;
              }
            }
          }
        }
        const { json } = result;

        if (!result.ok) {
          const message = json.description || json.message || 'Something went wrong. Please try again.';
          this.showError(message);
          if (deferred) {
            const cart = await this.fetchCart().catch(() => null);
            if (cart) {
              deferred.resolve({
                cart: Events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                detail: {
                  didError: true,
                  items: cart.items,
                  source: 'kv-product',
                  sourceId: this.dataset.sectionId,
                  itemCount: unitCount(items),
                  productId: this.dataset.productId,
                },
              });
            } else {
              deferred.reject(new Error(message));
            }
          }
          return;
        }

        added = true;

        if (deferred) {
          const cart = await this.fetchCart();
          deferred.resolve({
            cart: Events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              didError: false,
              items: cart.items,
              source: 'kv-product',
              sourceId: this.dataset.sectionId,
              itemCount: unitCount(addedItems),
              productId: this.dataset.productId,
              sections: json.sections,
            },
          });
        }

        this.announce('Added to cart');

        if (canUseDrawer) {
          // Open once the drawer's own listeners (registered earlier on the same promise) have
          // started morphing the fresh cart HTML, so an empty drawer doesn't flash first.
          const openDrawer = () => {
            if (!drawer.hasAttribute('open')) drawer.open();
          };
          deferred.promise.then(
            () => requestAnimationFrame(openDrawer),
            openDrawer
          );
        } else {
          window.location.href = this.cartUrl();
        }
      } catch (error) {
        if (deferred) deferred.reject(error);
        if (added) {
          // The item is in the cart but the drawer could not be refreshed: show the cart page.
          window.location.href = this.cartUrl();
        } else {
          this.showError('Something went wrong. Please check your connection and try again.');
        }
      } finally {
        this.busy = false;
        this.setLoading(false, triggerButton);
      }
    }

    /** Horizon's cart events module (import map `@shopify/events`), or null when unavailable. */
    async loadEvents() {
      try {
        const mod = await import('@shopify/events');
        if (mod && mod.CartLinesUpdateEvent && typeof mod.CartLinesUpdateEvent.createPromise === 'function') {
          return mod;
        }
      } catch (e) {
        /* fall through */
      }
      return null;
    }

    /**
     * Gift variant ids of an offer radio (data-gift-variants="id,id"; only available gifts are listed).
     * @param {HTMLInputElement | null | undefined} input
     * @returns {string[]}
     */
    giftIdsOf(input) {
      const raw = input && input.dataset.giftVariants;
      if (!raw) return [];
      return raw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    }

    /**
     * The free-gift lines to add with the offer `input`: one per gift of that offer that the cart does
     * not already grant for THIS offer, each tagged with the offer variants that grant that same gift.
     * Empty when the offer has no gift.
     * A gift line already in the cart is enough when its `_kv_gift_for` lists this offer. When it does
     * not (gift added from the other product page, e.g. menopause vs main, or by an older version of
     * this script), the gift is sent again with the union of both lists: the cart keeps one line per
     * gift (snippets/kv-cart-state.liquid: the newest line, which comes first, stays at 1; the older
     * one goes to 0), so the gift now follows every offer that grants it.
     * When the cart cannot be read every gift is included: the cart drawer brings any duplicate back
     * to quantity 1.
     * @param {HTMLInputElement | null} input
     */
    async giftItemsFor(input) {
      const giftIds = this.giftIdsOf(input);
      if (!giftIds.length) return [];

      const offerId = input ? String(input.value) : '';
      const cart = await this.fetchCart().catch(() => null);
      const lines = cart && Array.isArray(cart.items) ? cart.items : [];
      const splitIds = (value) =>
        String(value || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);

      const items = [];
      for (const giftId of giftIds) {
        const qualifying = this.offerInputs
          .filter((other) => this.giftIdsOf(other).includes(giftId))
          .map((other) => String(other.value));

        const existing = lines.filter((line) => String(line.variant_id) === giftId);
        const grantedFor = existing.map((line) => splitIds(line.properties && line.properties._kv_gift_for));
        // Already granted for this offer: nothing to add. A $0 gift line without a list does not count
        // (the cart removes it, see kv-cart-state), so the offer's own tagged line is sent.
        if (grantedFor.some((ids) => ids.includes(offerId))) continue;

        const merged = Array.from(new Set([...qualifying, ...grantedFor.flat()]));
        items.push({ id: Number(giftId), quantity: 1, properties: { _kv_gift_for: merged.join(',') } });
      }
      return items;
    }

    async fetchCart() {
      const res = await fetch(`${this.cartUrl()}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('cart fetch failed');
      return res.json();
    }

    /* ---------------------------------------------------------------- Editor */

    initEditor() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      const { signal } = this.ac;
      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const block = event.target;
          if (!(block instanceof HTMLElement) || !this.contains(block)) return;
          if (block.matches('details[data-faq]')) block.open = true;
        },
        { signal }
      );
    }
  }

  customElements.define('kv-product', KvProduct);
})();
